import moment from 'moment-timezone';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';

import { Resource } from '../../common/constants/resource';
import { getFormattedTimestamp } from '../../common/helpers';
import { ProductFactory } from '../../common/productConfig/productFactory';
import { TaskTypeToTaskTitleMapping } from '../tasks/types/taskTypes';
import { DecisionExplanation } from './DecisionExplanation';
import styles from './LinkedPolicies.module.scss';
import type { EvidenceDetails } from './linkedPolicies.utils';
import {
  formatCoverage,
  formatTaskStateLabel,
  useEvidenceOrdered,
} from './linkedPolicies.utils';
import {
  useApplicationDetailedLifecycleLabel,
  useApplicationStageLabel,
} from './useMetadataLabel';

export interface LinkedPolicyRow {
  key: string;
  policyNumberLabel: string;
  linkTo?: string;
  applicationId?: number;
  // The policy this row is about (Maestro's termPolicyId) - present even when there's no linked
  // Nora application, and used to key the Decision Explanation lookup.
  termPolicyId?: number;
  isBundled?: boolean;
  product?: string;
  carrier?: string;
  subCategory?: string;
  // Underwriting decision (e.g. RUW, Approved) — available for both existing
  // policies and past applications.
  decision?: string;
  decisionType?: string;
  decisionDate?: string;
  // Existing policies only: policy lifecycle/billing status (e.g. PREMIUM_PAYING).
  status?: string;
  effectiveDate?: string;
  coverage?: number;
  term?: number;
  stageV2?: string;
  detailedLifeCycleV2?: string;
  rateClass?: string;
  debits?: number;
  tobaccoClass?: string;
  notFound?: boolean;
}

const ExternalLinkIcon = () => (
  <svg
    className={styles.ViewPolicyIcon}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z"
    />
  </svg>
);

const EvidenceDetailsCell = ({
  details,
  fileOpenHandler,
}: {
  details?: EvidenceDetails;
  fileOpenHandler: (uri: string, fileName: string) => void;
}) => {
  if (!details) {
    return <>{Resource.NORA_M_DASH}</>;
  }
  if (details.kind === 'link') {
    return (
      <RouterLink
        to={details.href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.EvidenceDetails}
      >
        {details.label}
      </RouterLink>
    );
  }
  return (
    <button
      type="button"
      className={styles.EvidenceDetails}
      onClick={() => fileOpenHandler(details.uri, details.fileName)}
    >
      {details.label}
    </button>
  );
};

export const LinkedPoliciesItem = ({
  row,
  fileOpenHandler,
  defaultExpanded = false,
}: {
  row: LinkedPolicyRow;
  fileOpenHandler: (uri: string, fileName: string) => void;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const stageLabel = useApplicationStageLabel(row.stageV2);
  const detailedLifecycleLabel = useApplicationDetailedLifecycleLabel(
    row.detailedLifeCycleV2,
  );

  const { evidenceRows: fetchedEvidenceRows, isLoading: evidenceLoading } =
    useEvidenceOrdered(row.applicationId, expanded);
  const evidenceRows = fetchedEvidenceRows ?? [];
  const evidenceLoaded = fetchedEvidenceRows !== undefined;

  if (row.notFound) {
    return (
      <div className={styles.Item}>
        <div className={styles.NotFoundRow}>
          <Typography variant="body2">{row.policyNumberLabel}</Typography>
          <Typography variant="body2" color="text.secondary">
            {Resource.STACKED_POLICY_NOT_FOUND_ERROR}
          </Typography>
        </div>
      </div>
    );
  }

  const debitsLabel =
    row.debits != null
      ? row.debits > 0
        ? `+${row.debits}`
        : String(row.debits)
      : undefined;

  const workbenchStatusLabel = [detailedLifecycleLabel, stageLabel]
    .filter(Boolean)
    .join(' · ');

  // Badge beside the policy number: policy status + effective month/year
  // (existing policies only — past applications don't have either).
  const policyStatusLine = [
    row.status,
    row.effectiveDate
      ? moment(row.effectiveDate).format('MMM YYYY')
      : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  const productConfig = row.product
    ? ProductFactory.getProductConfig(
        row.carrier ?? '',
        row.product,
        row.subCategory ?? '',
      )
    : undefined;
  const productLabel = row.product
    ? (productConfig?.getProductDisplayText() ??
      `${row.carrier ?? ''} ${row.product}`.trim())
    : undefined;

  return (
    <Accordion
      className={styles.Item}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      square
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={styles.ItemSummary}
      >
        <div className={styles.ItemHeader}>
          <div className={styles.ItemHeaderMain}>
            {productLabel && (
              <Typography className={styles.ProductLabel}>
                {productLabel}
              </Typography>
            )}
            <Typography className={styles.ItemNumber}>
              #{row.policyNumberLabel}
            </Typography>
            {policyStatusLine && (
              <span className={styles.StatusBadge}>
                <span className={styles.StatusDot} />
                {policyStatusLine}
              </span>
            )}
            {row.isBundled && (
              <Typography variant="caption" color="text.secondary">
                (bundled)
              </Typography>
            )}
          </div>
          <div className={styles.ItemHeaderMeta}>
            {row.linkTo && (
              <RouterLink
                to={row.linkTo}
                target="_blank"
                onClick={e => e.stopPropagation()}
                className={styles.ViewPolicyLink}
              >
                View policy
                <ExternalLinkIcon />
              </RouterLink>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={styles.ItemDetails}>
        <Typography variant="overline" className={styles.SectionLabel}>
          Policy details
        </Typography>
        <div className={styles.CardGrid}>
          <div className={styles.Card}>
            <Typography variant="caption" className={styles.CardLabel}>
              Coverage
            </Typography>
            <Typography variant="body2" fontSize={13}>
              {formatCoverage(row.coverage)}
              {row.term ? ` · ${row.term} years` : ''}
            </Typography>
          </div>
          <div className={styles.Card}>
            <Typography variant="caption" className={styles.CardLabel}>
              Decision
            </Typography>
            <Typography variant="body2" fontSize={13}>
              {row.decision ?? Resource.NORA_M_DASH}
              {row.decisionType ? ` (${row.decisionType})` : ''}
              {row.decisionDate
                ? ` · ${moment(row.decisionDate).format('MMM D, YYYY')}`
                : ''}
            </Typography>
          </div>
          <div className={styles.Card}>
            <Typography variant="caption" className={styles.CardLabel}>
              Rate class
            </Typography>
            <Typography variant="body2" fontSize={13}>
              {row.rateClass ?? Resource.NORA_M_DASH}
              {debitsLabel ? ` ${debitsLabel}` : ''}
              {row.tobaccoClass ? ` · ${row.tobaccoClass}` : ''}
            </Typography>
          </div>
          <div className={styles.Card}>
            <Typography variant="caption" className={styles.CardLabel}>
              Workbench status
            </Typography>
            <Typography variant="body2" fontSize={13}>
              {workbenchStatusLabel || Resource.NORA_M_DASH}
            </Typography>
          </div>
        </div>

        <DecisionExplanation
          carrier={row.carrier}
          product={row.product}
          policyId={row.termPolicyId}
        />

        {row.applicationId ? (
          <>
            <Typography variant="overline" className={styles.SectionLabel}>
              Evidence ordered
            </Typography>
            {evidenceLoading && (
              <Typography variant="body2" color="text.secondary">
                Loading evidence…
              </Typography>
            )}
            {!evidenceLoading &&
              evidenceLoaded &&
              evidenceRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No evidence ordered for this policy.
                </Typography>
              )}
            {!evidenceLoading && evidenceRows.length > 0 && (
              <table className={styles.EvidenceTable}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Received on</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {evidenceRows.map(evidenceRow => (
                    <tr key={evidenceRow.id}>
                      <td>
                        {TaskTypeToTaskTitleMapping[evidenceRow.taskType]}
                      </td>
                      <td>{formatTaskStateLabel(evidenceRow.state)}</td>
                      <td>
                        {evidenceRow.receivedOn
                          ? getFormattedTimestamp(
                              evidenceRow.receivedOn,
                              'MMM D, YYYY h:mm A',
                            )
                          : Resource.NORA_M_DASH}
                      </td>
                      <td>
                        <EvidenceDetailsCell
                          details={evidenceRow.details}
                          fileOpenHandler={fileOpenHandler}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};
